// Data-dictionary findings types — wire shape from
// src/ai_alm/api/routes/data_dictionary_findings.py.

export type DataDictionaryFindingKind =
  | 'missing_mandatory_table'
  | 'missing_mandatory_column'
  | 'retention_tbd'
  | 'retention_below_floor'

export type DataDictionaryFindingSeverity = 'critical' | 'high' | 'medium'

export interface DataDictionaryFindingRow {
  finding_kind: DataDictionaryFindingKind
  severity: DataDictionaryFindingSeverity
  rule_source: string
  message: string
  cites: string[]
  table_name: string | null
  column_name: string | null
  suggested_fix: string | null
  /**
   * Concrete starting value that pre-fills the Apply-fix modal for a
   * retention finding. When `cites` is non-empty the value is the
   * profile's authoritative floor; otherwise it's a category-derived
   * provisional value the architect must confirm + cite. Advisory —
   * never part of `finding_key`.
   */
  suggested_retention: string | null
  /**
   * Blast radius (Stage 3, data-dict only): how many requirements
   * reference this finding's table (`len(referenced_by)`). Rendered
   * "backs N requirements". Null when the finding has no table.
   */
  backs_requirement_count: number | null
  /** Stable identity echoed back to the finding-waivers route. */
  finding_key: string
  /** True when an unexpired waiver already accepts this finding. */
  waived: boolean
  /** Accepted-waiver justification, when waived. */
  waiver_justification: string | null
}

export interface DataDictionaryFindingsResponse {
  findings: DataDictionaryFindingRow[]
  table_inventory: string[]
  regulatory: string[]
  shape: string | null
  data_dictionary_present: boolean
}

/** PEG invariant id this panel's findings belong to. */
export const DD_INVARIANT_ID = 'PEG-DATA-DICT-COMPLETENESS-001'

/** Finding kinds the Tier-2 retention apply-fix supports. */
export const DD_RETENTION_FIX_KINDS: DataDictionaryFindingKind[] = [
  'retention_tbd',
  'retention_below_floor',
]

export interface ApplyRetentionFixRequest {
  table_name: string
  retention_policy: string
}

// UI palette — severity → chip class
export const DD_SEVERITY_PALETTE: Record<
  DataDictionaryFindingSeverity,
  { label: string; chipClass: string }
> = {
  critical: {
    label: 'Critical',
    chipClass: 'bg-red-950/40 border-red-700/50 text-red-200',
  },
  high: {
    label: 'High',
    chipClass: 'bg-amber-950/40 border-amber-700/50 text-amber-200',
  },
  medium: {
    label: 'Medium',
    chipClass: 'bg-sky-950/40 border-sky-700/50 text-sky-200',
  },
}

// UI palette — finding-kind → human label
export const DD_KIND_LABEL: Record<DataDictionaryFindingKind, string> = {
  missing_mandatory_table: 'Missing required table',
  missing_mandatory_column: 'Missing required column',
  retention_tbd: 'Retention policy is TBD',
  retention_below_floor: 'Retention below regulatory floor',
}
